import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    stockKanbanContainersServices,
    type StockKanbanCompleteContainerParams,
    type StockKanbanContainer,
    type StockKanbanContainerContent,
    type StockKanbanCreateContentParams,
    type StockKanbanRemoveContentParams,
    type StockKanbanTakeForComplectParams,
    type StockKanbanUpdateContentParams,
} from "../../../../api/stockKanbanContainers";

/** Параметры хука мутаций карточки контейнера КАНБАН. */
export interface UseStockKanbanSupplyCardMutationsParams {
    /**
     * Заменить cache состава после create/update: полный список из `getLots`.
     * @param contents актуальные партии контейнера
     */
    onContentsReplace?: (contents: StockKanbanContainerContent[]) => void;
    /**
     * Патч cache после remove: удалить строку по коду.
     * @param contentCode код строки партии
     */
    onContentRemove?: (contentCode: number) => void;
    /**
     * Обновить карточку контейнера после взятия на комплектование.
     * @param container актуальный контейнер
     */
    onContainerTaken?: (container: StockKanbanContainer) => void;
    /** Колбэк успешного завершения контейнера. */
    onComplete?: () => void | Promise<unknown>;
}

/** Мутации карточки контейнера КАНБАН. */
export interface UseStockKanbanSupplyCardMutationsResult {
    /** Создание выбранной партии. */
    createContentMutation: UseMutationResult<StockKanbanContainerContent[], Error, StockKanbanCreateContentParams>;
    /** Обновление выбранной партии. */
    updateContentMutation: UseMutationResult<StockKanbanContainerContent[], Error, StockKanbanUpdateContentParams>;
    /** Удаление выбранной партии. */
    removeContentMutation: UseMutationResult<void, Error, StockKanbanRemoveContentParams>;
    /** Взятие контейнера на комплектование. */
    takeForComplectMutation: UseMutationResult<StockKanbanContainer, Error, StockKanbanTakeForComplectParams>;
    /** Завершение комплектования контейнера. */
    completeContainerMutation: UseMutationResult<void, Error, StockKanbanCompleteContainerParams>;
}

/** Screen-level мутации карточки контейнера КАНБАН. */
export function useStockKanbanSupplyCardMutations({
    onContentsReplace,
    onContentRemove,
    onContainerTaken,
    onComplete,
}: UseStockKanbanSupplyCardMutationsParams): UseStockKanbanSupplyCardMutationsResult {
    const createContentMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "content", "create"],
        meta: { errorSource: "stock.kanbanSupply.content.create" },
        mutationFn: (variables: StockKanbanCreateContentParams) => stockKanbanContainersServices.content.create(variables),
        onSuccess: (contents) => {
            notification.success("Партия добавлена");
            onContentsReplace?.(contents);
        },
    });

    const updateContentMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "content", "update"],
        meta: { errorSource: "stock.kanbanSupply.content.update" },
        mutationFn: (variables: StockKanbanUpdateContentParams) => stockKanbanContainersServices.content.update(variables),
        onSuccess: (contents) => {
            notification.success("Партия обновлена");
            onContentsReplace?.(contents);
        },
    });

    const removeContentMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "content", "remove"],
        meta: { errorSource: "stock.kanbanSupply.content.remove" },
        mutationFn: (variables: StockKanbanRemoveContentParams) => stockKanbanContainersServices.content.remove(variables),
        onSuccess: (_data, variables) => {
            notification.success("Выбор партии отменён");
            onContentRemove?.(variables.contentCode);
        },
    });

    const takeForComplectMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "takeForComplect"],
        meta: { errorSource: "stock.kanbanSupply.takeForComplect" },
        mutationFn: (variables: StockKanbanTakeForComplectParams) =>
            stockKanbanContainersServices.takeForComplect(variables),
        onSuccess: (container) => {
            notification.success("Контейнер взят на комплектование");
            onContainerTaken?.(container);
        },
    });

    const completeContainerMutation = useMutation({
        mutationKey: ["stock", "kanbanSupply", "completeContainer"],
        meta: { errorSource: "stock.kanbanSupply.completeContainer" },
        mutationFn: (variables: StockKanbanCompleteContainerParams) =>
            stockKanbanContainersServices.completeContainer(variables),
        onSuccess: async () => {
            notification.success("Комплектование контейнера завершено");
            await onComplete?.();
        },
    });

    return {
        createContentMutation,
        updateContentMutation,
        removeContentMutation,
        takeForComplectMutation,
        completeContainerMutation,
    };
}
